import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { UnifiedTable } from '../../../../components/UnifiedTable';
import { useApi } from '../../../../hooks/useApi';
import logger from '../../../../utils/logger';

const PAGE_SIZE = 50;

function OzonFbsStocksTable({ accountId }) {
    const { request, showStatus } = useApi();
    const [stocks, setStocks] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(1);
    const [pageSize, setPageSize] = useState(PAGE_SIZE);
    const [loading, setLoading] = useState(false);
    const [syncing, setSyncing] = useState(false);
    const [search, setSearch] = useState('');
    const searchRef = useRef('');

    const load = useCallback(async (p, ps, q = searchRef.current) => {
        setLoading(true);
        try {
            const data = await request(`/api/v1/ozon-ops/${accountId}/fbs/stocks?page=${p}&page_size=${ps}${q ? `&search=${encodeURIComponent(q)}` : ''}`);
            setStocks(Array.isArray(data?.items) ? data.items : []);
            setTotal(Number(data?.total || 0));
        } catch (e) {
            logger.error(e);
        } finally {
            setLoading(false);
        }
    }, [accountId, request]);

    const handleSync = useCallback(async () => {
        setSyncing(true);
        try {
            await request(`/api/v1/ozon-ops/${accountId}/fbs/stocks/sync`, { method: 'POST' });
            await load(1, pageSize);
            setPage(1);
            return true;
        } catch (e) {
            logger.error(e);
            showStatus('error', e?.message || 'Ошибка синхронизации остатков.');
            return false;
        } finally {
            setSyncing(false);
        }
    }, [accountId, request, showStatus, load, pageSize]);

    // Поиск идёт на сервере (по артикулу, SKU, названию, складу), с небольшой задержкой при вводе.
    useEffect(() => {
        if (!accountId || searchRef.current === search) return undefined;
        const timer = setTimeout(() => {
            searchRef.current = search;
            setPage(1);
            load(1, pageSize, search);
        }, 400);
        return () => clearTimeout(timer);
        // eslint-disable-next-line
    }, [search]);

    const handleSyncManual = async () => {
        if (await handleSync()) showStatus('success', 'Остатки обновлены.');
    };

    // Синхронизация происходит автоматически при заходе на вкладку — без ручного клика.
    // Сначала показываем то, что уже есть в БД (быстро), затем обновляем в фоне.
    useEffect(() => {
        if (!accountId) return;
        load(1, pageSize);
        setPage(1);
        handleSync();
        // eslint-disable-next-line
    }, [accountId]);

    const columns = useMemo(() => [
        { key: 'offer_id', header: 'Артикул', width: 160, type: 'text', sortable: false },
        { key: 'sku', header: 'SKU', width: 120, type: 'text', sortable: false },
        { key: 'name', header: 'Название', width: 280, type: 'text', sortable: false },
        {
            key: 'warehouse_text', header: 'Склад', width: 200, type: 'text', sortable: false,
        },
        { key: 'present', header: 'На складе', width: 110, type: 'text', sortable: false },
        { key: 'reserved', header: 'Резерв', width: 110, type: 'text', sortable: false },
        { key: 'available', header: 'Доступно', width: 110, type: 'text', sortable: false },
    ], []);

    const tableData = useMemo(() => stocks.map(s => ({
        ...s,
        warehouse_text: s.warehouse_name || s.warehouse_id || '—',
        offer_id: s.offer_id || '—',
        sku: s.sku || '—',
        name: s.name || '—',
    })), [stocks]);

    const toolbarActions = useMemo(() => ([
        { label: syncing ? 'Синхронизация…' : 'Синхронизировать остатки', onClick: handleSyncManual, disabled: syncing },
        // eslint-disable-next-line
    ]), [syncing]);

    return (
        <UnifiedTable
            tableId="ozon-fbs-stocks"
            columns={columns}
            data={tableData}
            totalRows={total}
            page={page}
            pageSize={pageSize}
            onPageChange={nextPage => { setPage(nextPage); load(nextPage, pageSize, search); }}
            onPageSizeChange={nextSize => { setPageSize(nextSize); setPage(1); load(1, nextSize, search); }}
            loading={loading}
            serverSort={true}
            search={search}
            onSearchChange={setSearch}
            toolbarActions={toolbarActions}
            onExportCsv={true}
            onCopyTable={true}
            getRowKey={(row) => row.id || `${row.offer_id}-${row.warehouse_id}`}
        />
    );
}

export default OzonFbsStocksTable;
